import { useEffect, useRef, useState } from 'react'
import {
  buildSimulationSchedule,
  buildGoalTimeline,
  deriveSimGameweekState,
  startSimulationLoop,
  type SimGameweekState,
  type SimulationScheduleEntry,
  type GoalEvent,
} from '../gameweekSimulation'
import type { Game } from '../types'

export interface StartSimulationOptions {
  games: Game[]
  /** That gameweek's real results, used to lay out each fixture's goal timeline. */
  results: Record<string, { homeScore: number; awayScore: number }>
  /** Runs once the playback reaches full time, after the local state has been cleared. */
  onFinish: () => void | Promise<void>
}

export interface GameweekSimulationController {
  isSimulating: boolean
  /** Derived live view of the playback, or null when nothing is running. */
  state: SimGameweekState | null
  gameStatuses: SimGameweekState['statuses'] | undefined
  liveResults: SimGameweekState['liveResults'] | undefined
  start: (options: StartSimulationOptions) => void
  /** Tear the playback down without finishing it — used when the admin jumps gameweeks. */
  stop: () => void
}

/**
 * The admin's "play Live" gameweek playback: a schedule, an elapsed-time
 * loop, and a per-fixture goal timeline, all local. It is purely the animation — the server-side
 * tick that scores the gameweek for every user runs alongside it and is the caller's business
 * (see `useDevTools`), which is why `start` takes only an `onFinish` callback.
 *
 * The loop is torn down on unmount so a navigation mid-playback cannot leave a timer running.
 */
export function useGameweekSimulation(): GameweekSimulationController {
  const [schedule, setSchedule] = useState<SimulationScheduleEntry[] | null>(null)
  const [elapsedMs, setElapsedMs] = useState(0)
  const [goalTimelines, setGoalTimelines] = useState<Record<string, GoalEvent[]>>({})
  const stopLoopRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    return () => {
      stopLoopRef.current?.()
    }
  }, [])

  const clear = () => {
    setSchedule(null)
    setElapsedMs(0)
    setGoalTimelines({})
  }

  const state = deriveSimGameweekState(schedule, elapsedMs, goalTimelines)

  return {
    isSimulating: schedule !== null,
    state,
    gameStatuses: state?.statuses,
    liveResults: state?.liveResults,

    stop: () => {
      stopLoopRef.current?.()
      stopLoopRef.current = null
      clear()
    },

    start: ({ games, results, onFinish }) => {
      const nextSchedule = buildSimulationSchedule(games)
      const timelines: Record<string, GoalEvent[]> = {}
      for (const entry of nextSchedule) {
        const game = games.find((g) => g.id === entry.gameId)
        const result = results[entry.gameId]
        if (game && result) {
          timelines[entry.gameId] = buildGoalTimeline(game, result, entry)
        }
      }
      setGoalTimelines(timelines)
      setSchedule(nextSchedule)
      setElapsedMs(0)

      stopLoopRef.current = startSimulationLoop(
        (ms) => setElapsedMs(ms),
        () => {
          stopLoopRef.current = null
          clear()
          void onFinish()
        }
      )
    },
  }
}
